import React, { useState, useEffect, useCallback } from 'react';
import liff from '@line/liff';
import { buildApiUrl } from '../../utils/apiBase';

const BRAND_ORANGE = '#FF6B35';

const TABS = [
  { key: 'all',     label: 'ทั้งหมด', icon: '💊' },
  { key: 'morning', label: 'เช้า',     icon: '🌅' },
  { key: 'noon',    label: 'กลางวัน',  icon: '☀️' },
  { key: 'evening', label: 'เย็น',     icon: '🌆' },
  { key: 'night',   label: 'ก่อนนอน', icon: '🌙' },
];

const TIME_LABELS = {
  morning: '🌅 เช้า',
  noon:    '☀️ กลางวัน',
  evening: '🌆 เย็น',
  night:   '🌙 ก่อนนอน',
};

const TIME_ORDER = ['morning', 'noon', 'evening', 'night'];

const MedicineNotifyPage = ({ appUser, onBack }) => {
  const [medicines, setMedicines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [toast, setToast] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const getAccessToken = () => {
    try { return liff.getAccessToken(); } catch { return null; }
  };

  const fetchMedicines = useCallback(async () => {
    const token = getAccessToken();
    if (!token) { setLoading(false); return; }
    setLoading(true);
    try {
      const res = await fetch(buildApiUrl('/api/medicines'), {
        headers: { Authorization: `Bearer ${token}` },
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'fetch failed');
      setMedicines(json.medicines || []);
    } catch (e) {
      console.error('fetchMedicines error:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchMedicines(); }, [fetchMedicines]);

  const handleDelete = async (note_key) => {
    const token = getAccessToken();
    try {
      const res = await fetch(buildApiUrl(`/api/medicines?note_key=${encodeURIComponent(note_key)}`), {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'delete failed');
      showToast('ลบยาแล้ว');
      setMedicines((prev) => prev.filter((m) => m.note_key !== note_key));
    } catch (e) {
      console.error('delete error:', e);
      showToast('ลบไม่สำเร็จ ลองอีกที', 'error');
    } finally {
      setConfirmDelete(null);
    }
  };

  const handleClose = () => {
    if (onBack) { onBack(); return; }
    try { liff.closeWindow(); } catch { window.history.back(); }
  };

  // Filtered list for active tab
  const visibleMeds = activeTab === 'all'
    ? medicines
    : medicines.filter((m) => m.times?.includes(activeTab));

  // Count per tab for badges
  const countBySlot = TIME_ORDER.reduce((acc, slot) => {
    acc[slot] = medicines.filter((m) => m.times?.includes(slot)).length;
    return acc;
  }, {});

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f7f8fa', paddingBottom: 100 }}>
      {/* Header */}
      <div style={{ backgroundColor: BRAND_ORANGE, position: 'sticky', top: 0, zIndex: 10 }}>
        <div style={{ padding: '20px 16px 0' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <p style={{ margin: 0, fontSize: 10, color: 'rgba(255,255,255,0.75)', fontWeight: 700, letterSpacing: '0.8px' }}>
                MEMMANY BOT
              </p>
              <h1 style={{ margin: '2px 0 0', fontSize: 20, fontWeight: 700, color: '#fff' }}>ยาของฉัน</h1>
              <p style={{ margin: '2px 0 0', fontSize: 11, color: 'rgba(255,255,255,0.8)' }}>
                {medicines.length > 0 ? `${medicines.length} รายการ` : 'ยาที่ต้องกิน'}
              </p>
            </div>
            <button
              onClick={handleClose}
              style={{ backgroundColor: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: 20, padding: '8px 16px', color: '#fff', fontWeight: 700, fontSize: 14, cursor: 'pointer' }}
            >
              กลับ
            </button>
          </div>
        </div>

        {/* Tabs — inside sticky header so they scroll with it */}
        {medicines.length > 0 && (
          <div style={{ display: 'flex', overflowX: 'auto', padding: '12px 12px 0', gap: 6, scrollbarWidth: 'none' }}>
            {TABS.filter((tab) => tab.key === 'all' || countBySlot[tab.key] > 0).map((tab) => {
              const isActive = activeTab === tab.key;
              const count = tab.key === 'all' ? medicines.length : countBySlot[tab.key];
              return (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  style={{
                    flexShrink: 0,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 5,
                    padding: '7px 14px',
                    borderRadius: '20px 20px 0 0',
                    border: 'none',
                    backgroundColor: isActive ? '#f7f8fa' : 'rgba(255,255,255,0.18)',
                    color: isActive ? BRAND_ORANGE : '#fff',
                    fontWeight: 700,
                    fontSize: 13,
                    cursor: 'pointer',
                    transition: 'background 0.15s',
                  }}
                >
                  <span>{tab.icon}</span>
                  <span>{tab.label}</span>
                  <span style={{
                    fontSize: 11,
                    fontWeight: 700,
                    backgroundColor: isActive ? BRAND_ORANGE : 'rgba(255,255,255,0.3)',
                    color: '#fff',
                    borderRadius: 10,
                    padding: '1px 7px',
                    marginLeft: 2,
                  }}>
                    {count}
                  </span>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Content */}
      <div style={{ padding: 16 }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '60px 0', color: '#aaa' }}>กำลังโหลด...</div>
        ) : medicines.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px 16px' }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>💊</div>
            <p style={{ color: '#888', fontSize: 15, lineHeight: 1.6 }}>
              ยังไม่มียาที่บันทึกไว้เลย<br />
              ส่งรูปฉลากยาให้ปลาทองในแชทแล้วบอกให้บันทึกได้เลย
            </p>
          </div>
        ) : visibleMeds.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px 16px', color: '#aaa' }}>
            ไม่มียาในช่วงเวลานี้
          </div>
        ) : (
          <>
            <div style={{ backgroundColor: '#fff', borderRadius: 14, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
              {visibleMeds.map((med, i) => (
                <div
                  key={med.note_key}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    padding: '14px 16px',
                    borderBottom: i < visibleMeds.length - 1 ? '0.5px solid #f0f0f0' : 'none',
                    backgroundColor: i % 2 === 0 ? '#fff' : '#fafafa',
                  }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{ margin: 0, fontWeight: 600, fontSize: 15, color: '#111', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {med.name}
                    </p>
                    <p style={{ margin: '3px 0 0', fontSize: 12, color: '#888' }}>{med.dosage}</p>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 6 }}>
                      {(med.times || [])
                        .sort((a, b) => TIME_ORDER.indexOf(a) - TIME_ORDER.indexOf(b))
                        .map((t) => (
                          <span
                            key={t}
                            style={{
                              fontSize: 11,
                              fontWeight: 600,
                              color: t === activeTab ? '#fff' : BRAND_ORANGE,
                              backgroundColor: t === activeTab ? BRAND_ORANGE : '#fff4f0',
                              borderRadius: 6,
                              padding: '2px 8px',
                            }}
                          >
                            {TIME_LABELS[t] ?? t}
                          </span>
                        ))}
                    </div>
                  </div>
                  <button
                    onClick={() => setConfirmDelete(med)}
                    style={{ padding: '6px 12px', borderRadius: 8, border: '1px solid #e53935', backgroundColor: 'transparent', color: '#e53935', fontWeight: 600, fontSize: 13, cursor: 'pointer', flexShrink: 0 }}
                  >
                    ลบ
                  </button>
                </div>
              ))}
            </div>

            <div style={{ backgroundColor: '#fff8f5', borderRadius: 14, padding: '14px 16px', marginTop: 12 }}>
              <p style={{ margin: 0, fontSize: 12, color: '#888', textAlign: 'center', lineHeight: 1.6 }}>
                ส่งรูปฉลากยาเพิ่มเติมในแชทกับบอทได้เลย<br />
                พิมพ์ <span style={{ fontWeight: 700, color: BRAND_ORANGE }}>ดูยา</span> หรือ <span style={{ fontWeight: 700, color: BRAND_ORANGE }}>ลบยา ชื่อยา</span>
              </p>
            </div>
          </>
        )}
      </div>

      {/* Delete confirm modal */}
      {confirmDelete && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(4px)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 2000 }}>
          <div style={{ backgroundColor: '#fff', padding: '25px', borderRadius: '16px', width: '90%', maxWidth: '400px', boxShadow: '0 20px 40px rgba(0,0,0,0.12)', textAlign: 'center' }}>
            <h3 style={{ margin: '0 0 12px 0', color: '#111' }}>ยืนยันการลบ</h3>
            <p style={{ color: '#888', marginBottom: '8px' }}>ต้องการลบยา</p>
            <p style={{ color: '#111', fontWeight: 700, fontSize: 16, marginBottom: '24px' }}>{confirmDelete.name}</p>
            <div style={{ display: 'flex', justifyContent: 'center', gap: '12px' }}>
              <button onClick={() => setConfirmDelete(null)} style={{ padding: '10px 20px', backgroundColor: '#f7f7f7', color: '#555', border: '1px solid #ddd', borderRadius: '8px', fontSize: 15, fontWeight: 600, cursor: 'pointer' }}>
                ยกเลิก
              </button>
              <button onClick={() => handleDelete(confirmDelete.note_key)} style={{ padding: '10px 20px', backgroundColor: '#e53935', color: '#fff', border: 'none', borderRadius: '8px', fontSize: 15, fontWeight: 600, cursor: 'pointer' }}>
                ลบยา
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div style={{ position: 'fixed', bottom: 30, left: '50%', transform: 'translateX(-50%)', backgroundColor: toast.type === 'error' ? '#e53935' : '#333', color: '#fff', padding: '12px 24px', borderRadius: 8, fontSize: 14, zIndex: 3000, whiteSpace: 'nowrap' }}>
          {toast.message}
        </div>
      )}
    </div>
  );
};

export default MedicineNotifyPage;
